import { useEffect, useRef } from 'react'
import type { RefObject } from 'react'

/**
 * Traps Tab inside `containerRef` while `active`, and puts focus back where it
 * came from on close — the dialog rule from docs/00-roadmap.md Part 3.
 *
 * Shared by the command palette, the shortcut cheatsheet and the mobile drawer
 * so all three behave identically.
 */

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([type="hidden"]):not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

function focusableIn(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    // A rail tooltip or a collapsed group's children are in the DOM but not
    // reachable; offsetParent is the cheap test for that.
    (element) => element.offsetParent !== null || getComputedStyle(element).position === 'fixed',
  )
}

export function useFocusTrap(
  active: boolean,
  containerRef: RefObject<HTMLElement | null>,
  onEscape?: (() => void) | undefined,
): void {
  // Kept in a ref so an inline arrow from the caller does not re-arm the trap
  // on every render — that would bounce focus back to the trigger mid-use.
  const onEscapeRef = useRef(onEscape)
  onEscapeRef.current = onEscape

  useEffect(() => {
    if (!active) return
    const container = containerRef.current
    if (!container) return

    const previouslyFocused = document.activeElement as HTMLElement | null

    // Focus the first control if the surface has not autofocused something.
    if (!container.contains(document.activeElement)) {
      const [first] = focusableIn(container)
      ;(first ?? container).focus()
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        // Stop an outer dialog from also closing on the same press.
        event.stopPropagation()
        onEscapeRef.current?.()
        return
      }

      if (event.key !== 'Tab') return

      const focusable = focusableIn(container)
      if (focusable.length === 0) {
        event.preventDefault()
        return
      }

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (!first || !last) return

      const activeElement = document.activeElement
      if (event.shiftKey && (activeElement === first || !container.contains(activeElement))) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      // Only steal focus back if it is still inside the surface being torn
      // down; a click elsewhere has already placed it deliberately.
      if (previouslyFocused?.isConnected === true && container.contains(document.activeElement)) {
        previouslyFocused.focus()
      }
    }
  }, [active, containerRef])
}
